/**
 * User Preferences Service
 * Handles saving and loading user preferences from Supabase
 */

import { supabase } from '@/lib/supabase';
import { asJson } from '@/lib/checklistJson';
import type { UserPreferences } from './user-preferences';

/**
 * Update user preferences in the database
 * @param preferences - Partial preferences object to update
 * @returns Promise with success status and error if any
 */
export async function updateUserPreferences(
  preferences: Partial<UserPreferences>
): Promise<{ success: boolean; error?: string }> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      return { success: false, error: 'User not authenticated' };
    }

    // Get current preferences
    const { data: currentProfile, error: fetchError } = await supabase
      .from('profiles')
      .select('preferences')
      .eq('id', user.id)
      .maybeSingle();

    if (fetchError) {
      console.error('[updateUserPreferences] Error fetching current preferences:', fetchError);
      return { success: false, error: fetchError.message };
    }

    // Merge with existing preferences
    const currentPreferences = (currentProfile?.preferences as UserPreferences) || {};
    const updatedPreferences: UserPreferences = {
      ...currentPreferences,
      ...preferences,
    };

    // Update preferences in database
    const { error: updateError } = await supabase
      .from('profiles')
      .update({
        preferences: asJson(updatedPreferences),
        updated_at: new Date().toISOString(),
      })
      .eq('id', user.id);

    if (updateError) {
      console.error('[updateUserPreferences] Error updating preferences:', updateError);
      return { success: false, error: updateError.message };
    }

    return { success: true };
  } catch (error) {
    console.error('[updateUserPreferences] Unexpected error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
}

/**
 * Get user preferences from the database
 * @returns Promise with user preferences or null if not found
 */
export async function getUserPreferences(): Promise<UserPreferences | null> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      return null;
    }

    const { data: profile, error } = await supabase
      .from('profiles')
      .select('preferences')
      .eq('id', user.id)
      .maybeSingle();

    if (error) {
      console.error('[getUserPreferences] Error fetching preferences:', error);
      return null;
    }

    return (profile?.preferences as UserPreferences) || null;
  } catch (error) {
    console.error('[getUserPreferences] Unexpected error:', error);
    return null;
  }
}
